/**
 * tilemapStore — the single source of truth shared by the framework-free paint
 * surface (TilemapSurface, imperative subscriber) and the React panels (hook
 * consumers). Built on zustand/vanilla so it works identically in and out of
 * React (_docs/tilemap/02 §2). This is the entire React ↔ non-React bridge.
 *
 * The heavy per-layer tile data (LayerTiles) and the UndoStack are held as
 * stable, deliberately non-reactive refs: mutating a Uint32Array does not — and
 * should not — trigger React renders; the surface renders imperatively. When
 * model data changes from OUTSIDE the surface's own paint loop (undo/redo,
 * chunk load, conflict reload), `renderTick` is bumped and the surface, which
 * subscribes to it, schedules a redraw.
 */

import { createStore } from 'zustand/vanilla';
import { useStore } from 'zustand';
import { LayerTiles } from '../model/chunk';
import { mergeLayersDown } from '../model/mergeLayers';
import { UndoStack, type FrozenStroke } from './strokes';
import type { TileID } from '../model/constants';
import type { Camera } from '../surface/camera';
import type { Layer, Stamp, Tileset, TilemapDocument, Tool, Viewport } from '../model/types';
import { tilemapClient, type ChunkFlush } from '../api/tilemapClient';

export type SaveStatus = 'idle' | 'dirty' | 'saving' | 'saved' | 'conflict' | 'error';

interface TilemapState {
    projectUuid: string | null;
    tilemapUuid: string | null;
    doc: TilemapDocument | null;

    // Non-reactive model refs (see file docblock).
    layerTiles: Map<string, LayerTiles>;
    undo: UndoStack;

    activeLayerUuid: string | null;
    tool: Tool;
    /** The current paint stamp (1×1 for a single tile; larger from the palette/picker). */
    stamp: Stamp;
    brushSize: number;
    camera: Camera;
    version: number;

    /**
     * The terrain being painted with the Autotile tool (a key into the
     * active layer's tileset `autotile_rules`), or null if none is selected
     * yet / the tileset has no terrains defined.
     */
    activeTerrain: string | null;

    status: SaveStatus;
    errorMessage: string | null;

    /** Bumped when model data changes outside the surface's paint loop. */
    renderTick: number;

    // Actions
    loadDocument: (projectUuid: string, tilemapUuid: string) => Promise<void>;
    /** Flush pending edits, then re-fetch the document (after a structural change). */
    reload: () => Promise<void>;
    close: () => void;
    setTool: (tool: Tool) => void;
    setActiveLayer: (uuid: string) => void;
    setStamp: (stamp: Stamp) => void;
    setSingleTile: (id: TileID) => void;
    setBrushSize: (n: number) => void;
    setCamera: (cam: Camera) => void;
    setActiveTerrain: (terrain: string | null) => void;
    /** The active layer's tileset, or undefined if not loaded yet. */
    activeTileset: () => Tileset | undefined;
    activeTiles: () => LayerTiles | undefined;
    tilesFor: (layerUuid: string) => LayerTiles | undefined;

    /** Called by tools after they mutate tile data during a live stroke. */
    markPainted: () => void;
    /** Commit a finished stroke to undo history (and mark dirty). */
    commitStroke: (stroke: FrozenStroke) => void;
    undoAction: () => void;
    redoAction: () => void;

    flush: () => Promise<void>;
    scheduleFlush: () => void;
    /** True if any layer still has unsaved chunk edits. */
    hasAnyDirty: () => boolean;

    /**
     * Merge one layer's painted tiles down onto the layer directly beneath it
     * (_docs/tilemap/07 TM5 polish), then delete the merged-away layer.
     * Requires both layers to share a tileset (mixing atlas indices from two
     * tilesets into one layer would render as the wrong tiles). Persists the
     * merge before deleting the layer, then reloads the document.
     */
    mergeLayerDown: (upperLayerUuid: string) => Promise<{ ok: boolean; error?: string }>;
}

const AUTOSAVE_DELAY_MS = 1500;

export const tilemapStore = createStore<TilemapState>((set, get) => {
    let saveTimer: ReturnType<typeof setTimeout> | null = null;

    return {
        projectUuid: null,
        tilemapUuid: null,
        doc: null,
        layerTiles: new Map(),
        undo: new UndoStack(),
        activeLayerUuid: null,
        tool: 'brush',
        stamp: { w: 1, h: 1, tiles: [1] },
        brushSize: 1,
        camera: { x: -1, y: -1, zoom: 24 },
        version: 0,
        status: 'idle',
        errorMessage: null,
        renderTick: 0,
        activeTerrain: null,

        loadDocument: async (projectUuid, tilemapUuid) => {
            set({ status: 'idle', errorMessage: null });
            try {
                const doc = await tilemapClient.getTilemap(projectUuid, tilemapUuid);

                // Fresh model state.
                const layerTiles = new Map<string, LayerTiles>();
                for (const layer of doc.layers) layerTiles.set(layer.uuid, new LayerTiles());

                const undo = new UndoStack();
                const activeLayerUuid = doc.layers[0]?.uuid ?? null;
                const camera = viewportToCamera(doc.tilemap.viewport) ?? { x: -1, y: -1, zoom: 24 };

                set({
                    projectUuid,
                    tilemapUuid,
                    doc,
                    layerTiles,
                    undo,
                    activeLayerUuid,
                    version: doc.tilemap.version,
                    camera,
                    status: 'idle',
                    renderTick: get().renderTick + 1,
                });

                // Load painted chunks for every layer (TM1: all of them; a
                // camera-range fetch is a later optimization). Sequential per
                // layer keeps it simple; each is one range-less request.
                await Promise.all(
                    doc.layers.map(async (layer) => {
                        if (layer.chunk_index.length === 0) return;
                        const res = await tilemapClient.getChunks(projectUuid, tilemapUuid, layer.uuid);
                        const tiles = layerTiles.get(layer.uuid)!;
                        for (const c of res.chunks) tiles.loadChunkBase64(c.cx, c.cy, c.data);
                    }),
                );
                set({ renderTick: get().renderTick + 1 });
            } catch (err) {
                set({ status: 'error', errorMessage: (err as Error).message });
            }
        },

        reload: async () => {
            const { projectUuid, tilemapUuid } = get();
            if (!projectUuid || !tilemapUuid) return;
            await get().flush(); // persist any pending edits before re-fetching
            await get().loadDocument(projectUuid, tilemapUuid);
        },

        mergeLayerDown: async (upperLayerUuid) => {
            const { doc, layerTiles } = get();
            if (!doc) return { ok: false, error: 'No map is open.' };

            const ordered = orderedLayers(doc); // bottom-to-top
            const idx = ordered.findIndex((l) => l.uuid === upperLayerUuid);
            if (idx <= 0) {
                return { ok: false, error: 'There is no layer beneath this one to merge into.' };
            }

            const upperMeta = ordered[idx];
            const lowerMeta = ordered[idx - 1];
            if (upperMeta.tileset_uuid !== lowerMeta.tileset_uuid) {
                return { ok: false, error: 'Merge down requires both layers to use the same tileset.' };
            }

            const upperTiles = layerTiles.get(upperMeta.uuid);
            const lowerTiles = layerTiles.get(lowerMeta.uuid);
            if (!upperTiles || !lowerTiles) {
                return { ok: false, error: 'Layer data is not loaded yet — try again in a moment.' };
            }

            mergeLayersDown(upperTiles, lowerTiles);
            set({ renderTick: get().renderTick + 1 });

            // Persist the merged lower layer before deleting the upper one.
            await get().flush();
            try {
                await tilemapClient.deleteLayer(upperMeta.uuid);
            } catch (err) {
                return { ok: false, error: (err as Error).message };
            }

            await get().reload();
            return { ok: true };
        },

        close: () => {
            if (saveTimer) {
                clearTimeout(saveTimer);
                saveTimer = null;
            }
            set({
                projectUuid: null,
                tilemapUuid: null,
                doc: null,
                layerTiles: new Map(),
                undo: new UndoStack(),
                activeLayerUuid: null,
                status: 'idle',
                errorMessage: null,
            });
        },

        setTool: (tool) => set({ tool }),
        setActiveLayer: (uuid) => set({ activeLayerUuid: uuid, activeTerrain: null }),
        setStamp: (stamp) => set({ stamp }),
        setSingleTile: (id) => set({ stamp: { w: 1, h: 1, tiles: [id] } }),
        setActiveTerrain: (terrain) => set({ activeTerrain: terrain }),
        activeTileset: () => {
            const { doc, activeLayerUuid } = get();
            if (!doc) return undefined;
            const layer = doc.layers.find((l) => l.uuid === activeLayerUuid);
            return doc.tilesets.find((t) => t.uuid === layer?.tileset_uuid);
        },
        setBrushSize: (n) => set({ brushSize: Math.max(1, Math.min(16, n)) }),
        setCamera: (camera) => {
            set({ camera });
            get().scheduleFlush(); // viewport rides the next chunk flush as meta
        },

        activeTiles: () => {
            const { activeLayerUuid, layerTiles } = get();
            return activeLayerUuid ? layerTiles.get(activeLayerUuid) : undefined;
        },
        tilesFor: (layerUuid) => get().layerTiles.get(layerUuid),

        markPainted: () => {
            if (get().status !== 'saving') set({ status: 'dirty' });
            get().scheduleFlush();
        },

        commitStroke: (stroke) => {
            if (stroke.edits.length === 0) return;
            get().undo.push(stroke);
            get().markPainted();
        },

        undoAction: () => {
            const changed = get().undo.undo((uuid) => get().layerTiles.get(uuid));
            if (changed) {
                set({ renderTick: get().renderTick + 1, status: 'dirty' });
                get().scheduleFlush();
            }
        },

        redoAction: () => {
            const changed = get().undo.redo((uuid) => get().layerTiles.get(uuid));
            if (changed) {
                set({ renderTick: get().renderTick + 1, status: 'dirty' });
                get().scheduleFlush();
            }
        },

        scheduleFlush: () => {
            if (saveTimer) clearTimeout(saveTimer);
            saveTimer = setTimeout(() => {
                void get().flush();
            }, AUTOSAVE_DELAY_MS);
        },

        flush: async () => {
            const { projectUuid, tilemapUuid, layerTiles, version, camera, status } = get();
            if (!projectUuid || !tilemapUuid || status === 'saving') return;

            // Gather dirty chunks across all layers. Drain clears them; on
            // failure we re-queue so the next flush retries (no lost edits).
            const flushes: ChunkFlush[] = [];
            const drained: Array<{ layer: string; cx: number; cy: number }> = [];
            for (const [layerUuid, tiles] of layerTiles) {
                for (const c of tiles.drainDirtyForSave()) {
                    flushes.push({ layer: layerUuid, cx: c.cx, cy: c.cy, data: c.data });
                    drained.push({ layer: layerUuid, cx: c.cx, cy: c.cy });
                }
            }

            const meta = { viewport: cameraToViewport(camera) };
            if (flushes.length === 0) {
                // Nothing painted — still persist the camera cheaply.
                try {
                    await tilemapClient.saveChunks(projectUuid, tilemapUuid, version, [], meta);
                    // version unchanged for an empty flush unless server bumped it;
                    // re-read is unnecessary here.
                } catch { /* viewport save is non-critical */ }
                return;
            }

            set({ status: 'saving' });
            try {
                const result = await tilemapClient.saveChunks(projectUuid, tilemapUuid, version, flushes, meta);
                if (result.ok) {
                    set({ status: get().hasAnyDirty() ? 'dirty' : 'saved', version: result.version ?? version });
                    if (get().hasAnyDirty()) get().scheduleFlush();
                } else {
                    // 409: re-queue what we tried to save, adopt the server
                    // version, and surface the conflict. Re-fetching to merge
                    // is a TM-later refinement; retrying with the fresh version
                    // resolves the common two-tab case.
                    for (const d of drained) get().layerTiles.get(d.layer)?.requeueForSave(d.cx, d.cy);
                    set({ status: 'conflict', version: result.conflictVersion ?? version });
                    get().scheduleFlush();
                }
            } catch (err) {
                for (const d of drained) get().layerTiles.get(d.layer)?.requeueForSave(d.cx, d.cy);
                set({ status: 'error', errorMessage: (err as Error).message });
            }
        },

        hasAnyDirty: () => {
            for (const tiles of get().layerTiles.values()) {
                if (tiles.hasSaveDirty()) return true;
            }
            return false;
        },
    };
});

// ── viewport <-> camera helpers ──────────────────────────────────────────────

function cameraToViewport(cam: Camera): Viewport {
    return { x: cam.x, y: cam.y, zoom: cam.zoom };
}

function viewportToCamera(vp: Viewport | null): Camera | null {
    if (!vp) return null;
    return { x: vp.x, y: vp.y, zoom: vp.zoom };
}

/** Convenience: layer list in draw order (bottom-to-top). */
export function orderedLayers(doc: TilemapDocument | null): Layer[] {
    return doc ? [...doc.layers].sort((a, b) => a.sort_order - b.sort_order) : [];
}

/** React hook wrapper. */
export function useTilemapStore<T>(selector: (s: TilemapState) => T): T {
    return useStore(tilemapStore, selector);
}
